import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import classnames from 'classnames';
import {
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
} from 'reactstrap';
import { Icon } from 'js/components';
import {
  faCaretRight,
  faCaretDown,
  faCircleSmall,
} from '@fortawesome/pro-solid-svg-icons';
import {
  draggable,
  dropTargetForElements,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import { delay } from 'js/utils/delay';
import useWindowDimensions from 'js/utils/window-dimensions-hook';
import RenameForm from './RenameForm';
import { OrgTreeContext } from './OrgTreeContext';
import TeamNode from './TeamNode';
import { mountOrgTreeDragPreview } from './OrgTreeDragPreview';
import {
  sortChildren,
  canStartDragFromOrgTreeRow,
  canDropSourceOnOrgFolder,
  orgRowDropHighlightKey,
} from './org-tree-utils';

function OrgNode({
  node,
  pathToNode = [],
  depth = 0,
  onExpand,
  onCollapse,
  onAddTeams,
  onAddGroup,
  onAddOrgRef,
  onRemove,
  onRename,
  onPromoteToOrg,
}) {
  const isTreeRoot = pathToNode.length === 0;
  const childPath = [...pathToNode, node];
  const fromParentId = pathToNode[pathToNode.length - 1]?.id;

  const { find, dispatch, uniqueContextId, activeDropHighlightKey } =
    useContext(OrgTreeContext);
  const nodeTreeState = find(node.depthId);
  const open = nodeTreeState?.isOpen;

  const [showMenu, setShowMenu] = useState(false);
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [editName, setEditName] = useState(false);
  const windowDims = useWindowDimensions();

  const sortedChildren = useMemo(
    () => sortChildren(node.children),
    [node.children]
  );
  const childrenCount = node.children.length;

  const folderDropHighlight = useMemo(
    () => activeDropHighlightKey === orgRowDropHighlightKey(node.depthId),
    [activeDropHighlightKey, node.depthId]
  );

  const toggleChildren = useCallback(() => {
    if (childrenCount > 0) {
      dispatch({ type: 'toggle', itemId: node.depthId });
    }
  }, [dispatch, node.depthId, childrenCount]);

  const treeIconProps = useMemo(() => {
    if (childrenCount === 0) {
      return { icon: faCircleSmall, transform: 'shrink-9' };
    }
    return open ? { icon: faCaretDown } : { icon: faCaretRight };
  }, [open, childrenCount]);

  const handleRename = useCallback(
    (name) => {
      return Promise.resolve(onRename(pathToNode, node, name)).finally(() =>
        setEditName(false)
      );
    },
    [onRename, node, pathToNode]
  );

  const dragRef = useRef(null);
  const dropTargetRef = useRef(null);
  const cancelExpandRef = useRef(null);
  const cancelExpand = useCallback(() => {
    cancelExpandRef.current?.();
    cancelExpandRef.current = null;
  }, []);

  useEffect(() => {
    if (!dropTargetRef.current) {
      return undefined;
    }

    const dropTargetArgs = {
      element: dropTargetRef.current,
      getData: () => ({
        type: 'org-drop',
        uniqueContextId,
        node,
        highlightKey: orgRowDropHighlightKey(node.depthId),
      }),
      onDragLeave: () => {
        cancelExpand();
      },
      onDrop: () => {
        cancelExpand();
      },
    };

    if (isTreeRoot) {
      return combine(
        dropTargetForElements({
          ...dropTargetArgs,
          canDrop: ({ source }) =>
            source.data.uniqueContextId === uniqueContextId,
          onDrag: ({ source }) => {
            const sourceType = source.data.type;
            if (
              source.data.uniqueContextId === uniqueContextId &&
              (sourceType === 'team' || sourceType === 'org-node') &&
              childrenCount &&
              !open &&
              !cancelExpandRef.current
            ) {
              cancelExpandRef.current = delay({
                waitMs: 500,
                fn: () => dispatch({ type: 'expand', itemId: node.depthId }),
              });
            }
          },
        })
      );
    }

    if (!dragRef.current) {
      return undefined;
    }

    return combine(
      draggable({
        element: dragRef.current,
        canDrag: canStartDragFromOrgTreeRow,
        getInitialData: () => ({
          type: 'org-node',
          uniqueContextId,
          node,
          fromParentId,
          pathToNode,
          isOpenOnDragStart: open,
        }),
        onGenerateDragPreview: (args) => {
          mountOrgTreeDragPreview(args, {
            kind: node.isRoot ? 'organization' : 'group',
            name: node.name,
          });
        },
        onDragStart: ({ source }) => {
          source.element.classList.add('org-tree-row--dragging');
          if (source.data.isOpenOnDragStart) {
            dispatch({ type: 'collapse', itemId: node.depthId });
          }
        },
        onDrop: ({ source }) => {
          source.element.classList.remove('org-tree-row--dragging');
          if (source.data.isOpenOnDragStart) {
            dispatch({ type: 'expand', itemId: node.depthId });
          }
        },
      }),
      dropTargetForElements({
        ...dropTargetArgs,
        canDrop: ({ source }) =>
          canDropSourceOnOrgFolder(source, node, uniqueContextId),
        onDrag: ({ source }) => {
          if (
            canDropSourceOnOrgFolder(source, node, uniqueContextId) &&
            childrenCount &&
            !open &&
            !cancelExpandRef.current
          ) {
            cancelExpandRef.current = delay({
              waitMs: 500,
              fn: () => dispatch({ type: 'expand', itemId: node.depthId }),
            });
          }
        },
      })
    );
  }, [
    isTreeRoot,
    uniqueContextId,
    node,
    open,
    cancelExpand,
    childrenCount,
    dispatch,
    fromParentId,
    pathToNode,
  ]);

  const sharedChildProps = {
    onExpand,
    onCollapse,
    onAddTeams,
    onAddGroup,
    onAddOrgRef,
    onRemove,
    onRename,
    onPromoteToOrg,
  };

  return (
    <div
      ref={dropTargetRef}
      className={classnames('org-node', {
        'drop-target': folderDropHighlight,
      })}
    >
      <div
        className={classnames('row', {
          editing: editName,
          'with-menu-open': showMenu || showAddMenu,
        })}
        ref={dragRef}
      >
        <div className="gutters">
          {pathToNode.map((n) => (
            <div
              key={n.depthId}
              className={`gutter ${n.isRoot ? 'org-level' : 'group-level'}`}
            />
          ))}
        </div>
        <div className="icons" style={{ display: 'flex' }}>
          <button
            type="button"
            className="btn btn-link folder-toggle"
            onClick={toggleChildren}
          >
            <Icon
              size="sm"
              fixedWidth
              className="tree-icon"
              {...treeIconProps}
            />
          </button>
          <button
            type="button"
            className="btn btn-link folder-toggle"
            onClick={toggleChildren}
          >
            {node.isRoot ? (
              <Icon className="row-icon org-icon" icon="sitemap" />
            ) : (
              <Icon
                className="row-icon folder-icon"
                icon="user-friends"
                mask="folder"
                transform="shrink-9 down-1"
              />
            )}
          </button>
        </div>
        <div className="row-content">
          <div className="name">
            {editName ? (
              <RenameForm
                node={node}
                onCancel={() => setEditName(false)}
                onSave={handleRename}
              />
            ) : (
              <span>{node.name}</span>
            )}
          </div>
          {!editName && (
            <div className="actions">
              <Dropdown
                size="sm"
                isOpen={showMenu}
                toggle={() => setShowMenu(!showMenu)}
              >
                <DropdownToggle className="btn btn-link">
                  <Icon icon={['far', 'ellipsis-h']} />
                </DropdownToggle>
                <DropdownMenu right={windowDims.width <= 600}>
                  <DropdownItem onClick={() => onExpand(node, true)}>
                    <Icon icon="expand-alt" className="mx-1" /> Expand all
                  </DropdownItem>
                  <DropdownItem onClick={() => onCollapse(node, true)}>
                    <Icon icon="compress-alt" className="mx-1" /> Collapse all
                  </DropdownItem>
                  <DropdownItem onClick={() => setEditName(true)}>
                    <Icon icon="pencil-alt" className="mx-1" /> Rename
                  </DropdownItem>
                  {!isTreeRoot && (
                    <>
                      <DropdownItem
                        disabled={depth === 0}
                        onClick={() => onRemove(pathToNode, node)}
                      >
                        <Icon icon="trash-alt" className="mx-1" /> Remove
                      </DropdownItem>
                      {!node.isRoot && (
                        <DropdownItem onClick={() => onPromoteToOrg(node)}>
                          <Icon icon="sitemap" className="mx-1" /> Promote to
                          organization
                        </DropdownItem>
                      )}
                    </>
                  )}
                </DropdownMenu>
              </Dropdown>
              <Dropdown
                size="sm"
                isOpen={showAddMenu}
                toggle={() => setShowAddMenu(!showAddMenu)}
              >
                <DropdownToggle
                  className="btn btn-link"
                  data-test="node-add-btn"
                >
                  <Icon icon={['fas', 'plus']} />
                </DropdownToggle>
                <DropdownMenu right={windowDims.width <= 600}>
                  <DropdownItem
                    data-test="add-group-btn"
                    onClick={() => onAddGroup(node)}
                  >
                    <Icon
                      className="folder-icon mx-1"
                      icon="user-friends"
                      mask="folder"
                      transform="shrink-9 down-1"
                    />
                    <span style={{ position: 'relative', bottom: '2px' }}>
                      {' '}
                      Group
                    </span>
                  </DropdownItem>
                  <DropdownItem onClick={() => onAddOrgRef(node)}>
                    <Icon icon="sitemap" className="mx-1 org-icon" />{' '}
                    Organization
                  </DropdownItem>
                  <DropdownItem onClick={() => onAddTeams(node)}>
                    <Icon
                      icon="users"
                      data-test="add-teams-btn"
                      className="team-icon mx-1"
                    />{' '}
                    Team(s)
                  </DropdownItem>
                </DropdownMenu>
              </Dropdown>
            </div>
          )}
        </div>
      </div>
      {open && (
        <>
          {sortedChildren.map((child) => {
            if (child.type === 'org') {
              return (
                <OrgNode
                  key={`p-${node.id}-o-${child.id}`}
                  node={child}
                  pathToNode={childPath}
                  depth={depth + 1}
                  {...sharedChildProps}
                />
              );
            }
            return (
              <TeamNode
                key={`p-${node.id}-t-${child.id}`}
                node={child}
                pathToNode={childPath}
                depth={depth + 1}
                onRemove={onRemove}
              />
            );
          })}
        </>
      )}
    </div>
  );
}

export default OrgNode;
